import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api.js';
import { useI18n } from '../../i18n/index.jsx';
import { Alert } from '../../components/ui.jsx';
import { TaskModal } from './CustomerPage.jsx';
import { fmtDate, today } from './ws.jsx';
import TutorialSlot from '../../tutorials/TutorialSlot.jsx';

// Tasks & follow-ups: a list, and a month calendar (tasks + customers'
// next follow-up dates). An email reminder goes out on the due date.
export default function Tasks({ base = '/account/workspace' }) {
  const { t, lang, errorText } = useI18n();
  const [view, setView] = useState('list');
  const [data, setData] = useState(null);
  const [scope, setScope] = useState('mine');
  const [showDone, setShowDone] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(() => api(`/workspace/tasks?scope=${scope}${showDone ? '&done=1' : ''}`).then(setData).catch((e) => setError(errorText(e))), [scope, showDone, errorText]);
  useEffect(() => { load(); }, [load]);
  async function patch(x, body) { await api(`/workspace/tasks/${x.id}`, { method: 'PATCH', body }).catch(() => {}); load(); }
  async function remove(x) { await api(`/workspace/tasks/${x.id}`, { method: 'DELETE' }).catch(() => {}); load(); }
  const late = (x) => !x.done && x.dueOn && x.dueOn < today();
  return (
    <div className="ws-page">
      <div className="acc-head"><TutorialSlot place="ws.tasks" />
        <div><h1>{t('ws.tasks.title')}</h1><p className="muted">{t('ws.tasks.lead')}</p></div>
        <div className="acc-head-actions"><button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>+ {t('ws.tasks.add')}</button></div>
      </div>
      <Alert>{error}</Alert>
      <div className="acc-toolbar">
        <div className="acc-seg" role="group">
          <button type="button" aria-pressed={view === 'list'} onClick={() => setView('list')}>{t('ws.tasks.list')}</button>
          <button type="button" aria-pressed={view === 'calendar'} onClick={() => setView('calendar')}>{t('ws.tasks.calendar')}</button>
        </div>
        <select value={scope} onChange={(e) => setScope(e.target.value)} aria-label={t('ws.tasks.scope')}><option value="mine">{t('ws.tasks.mine')}</option><option value="all">{t('ws.tasks.all')}</option></select>
        {view === 'list' && <label className="field-checkbox"><input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} /><span>{t('ws.tasks.showDone')}</span></label>}
      </div>
      {view === 'list' && data && (
        <ul className="ws-list ws-card">
          {data.tasks.map((x) => (
            <li key={x.id} className={x.done ? 'muted' : ''}>
              <input type="checkbox" checked={x.done} onChange={() => patch(x, { done: !x.done })} aria-label={x.title} />
              <span className="ws-grow">
                <span>{x.title}</span>
                {x.customer && <> — <Link to={`${base}/customers/${x.crmId}`} className="small">{x.customer}</Link></>}
                {scope === 'all' && x.assignee && <span className="muted small"> · {x.assignee}</span>}
              </span>
              <input type="date" value={x.dueOn || ''} onChange={(e) => patch(x, { dueOn: e.target.value })} aria-label={t('ws.tasks.due')} className={late(x) ? 'field-error' : ''} style={{ width: 150 }} />
              <button type="button" className="btn btn-link btn-sm" onClick={() => remove(x)} aria-label={t('ws.tasks.delete')}>✕</button>
            </li>
          ))}
          {!data.tasks.length && <li className="muted">{t('ws.tasks.empty')}</li>}
        </ul>
      )}
      {view === 'calendar' && <Calendar scope={scope} base={base} lang={lang} />}
      {adding && <TaskModal assignees={data?.assignees || []} canAssign={scope === 'all'} onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
    </div>
  );
}

function Calendar({ scope, base, lang }) {
  const { t } = useI18n();
  const [month, setMonth] = useState(today().slice(0, 7));
  const [items, setItems] = useState([]);
  useEffect(() => { api(`/workspace/calendar?month=${month}&scope=${scope}`).then((d) => setItems(d.items)).catch(() => setItems([])); }, [month, scope]);
  const [y, m] = month.split('-').map(Number);
  const first = new Date(y, m - 1, 1);
  const start = (first.getDay() + 6) % 7; // Monday first
  const days = new Date(y, m, 0).getDate();
  const cells = [...Array(start).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  while (cells.length % 7) cells.push(null);
  const shift = (d) => { const x = new Date(y, m - 1 + d, 1); setMonth(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`); };
  const names = Array.from({ length: 7 }, (_, i) => new Date(2024, 0, 1 + i).toLocaleDateString(lang === 'en' ? 'en-CA' : 'fr-CA', { weekday: 'short' }));
  return (
    <section className="ws-card">
      <div className="ws-row">
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => shift(-1)} aria-label={t('ws.tasks.prev')}>‹</button>
        <h2 className="ws-grow" style={{ textAlign: 'center' }}>{first.toLocaleDateString(lang === 'en' ? 'en-CA' : 'fr-CA', { month: 'long', year: 'numeric' })}</h2>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => shift(1)} aria-label={t('ws.tasks.next')}>›</button>
      </div>
      <div className="ws-cal">
        {names.map((n) => <div key={n} className="ws-cal-head">{n}</div>)}
        {cells.map((d, i) => {
          const iso = d ? `${month}-${String(d).padStart(2, '0')}` : null;
          const list = iso ? items.filter((x) => x.day === iso) : [];
          return (
            <div key={i} className={`ws-cal-day${!d ? ' is-out' : ''}${iso === today() ? ' is-today' : ''}`}>
              {d && <span className="ws-cal-n">{d}</span>}
              {list.map((x) => (
                <Link key={`${x.type}${x.id}`} to={x.crmId ? `${base}/customers/${x.crmId}` : `${base}/tasks`} className={`ws-cal-item${x.type === 'followup' ? ' is-followup' : ''}${x.done ? ' is-done' : ''}`} title={x.title}>
                  {x.type === 'followup' ? '↻ ' : ''}{x.title}
                </Link>
              ))}
            </div>
          );
        })}
      </div>
      <p className="ws-hint">{t('ws.tasks.legend')}</p>
    </section>
  );
}
